import { ReactElement, useMemo, useRef } from 'react';
import styles from './region-selector.module.scss';
import { useCommonTranslation } from '../hooks';
import {
    UIIcon,
    UIMenu,
    UIMenuItem,
    UIPopover,
    UIPropoverRef,
    UIText
} from '@/ui';
import { useRegion } from '../providers/region-provider';
import { useNullableTokens } from '../providers/tokens-provider';
import {
    RegionOption,
    ResolvedRegion
} from '../providers/region-provider/models';
import { MULTI_REGION_ENABLED } from '../providers/region-provider/constants';
import ukEuFlag from '../assets/images/uk-eu-flag.png';
import usFlag from '../assets/images/us-flag.png';

type Variant = 'floating' | 'compact';

type RegionSelectorProps = {
    variant?: Variant;
};

const REGION_FLAGS: Record<ResolvedRegion, string> = {
    eu: ukEuFlag,
    us: usFlag
};

export function RegionSelector({
    variant = 'floating'
}: RegionSelectorProps): ReactElement {
    const { t } = useCommonTranslation();

    const regionLabels: Record<RegionOption, string> = useMemo(
        () => ({
            auto: t('components.region_selector.auto'),
            eu: t('components.region_selector.eu'),
            us: t('components.region_selector.us')
        }),
        [t]
    );
    const { tokens } = useNullableTokens();
    const { selectedRegion, autoDetectedRegion, isLoading, setRegion } =
        useRegion();
    const popoverRef = useRef<UIPropoverRef>(null);

    // Don't render if multi-region is disabled or user is authenticated
    if (!MULTI_REGION_ENABLED || tokens) {
        return <></>;
    }

    const handleRegionSelect = (region: RegionOption) => {
        setRegion(region);
        popoverRef.current?.close();
    };

    const displayLabel =
        selectedRegion === 'auto'
            ? `${regionLabels.auto} (${regionLabels[autoDetectedRegion]})`
            : regionLabels[selectedRegion];

    const flagWidth = variant === 'compact' ? 18 : 24;
    const flagHeight = variant === 'compact' ? 9 : 12;
    const displayFlag =
        selectedRegion === 'auto'
            ? REGION_FLAGS[autoDetectedRegion]
            : REGION_FLAGS[selectedRegion];

    const triggerButton = (
        <button className={styles.trigger}>
            {isLoading ? (
                <UIIcon name="globe" size={flagHeight} />
            ) : (
                <img
                    src={displayFlag}
                    alt=""
                    className={styles.flag}
                    style={{ width: flagWidth, height: 'auto' }}
                />
            )}
            <UIText variant={variant === 'compact' ? 'small' : 'basic'}>
                {isLoading
                    ? t('components.region_selector.detecting')
                    : displayLabel}
            </UIText>
            <UIIcon
                name="chevron-down"
                size={variant === 'compact' ? 12 : 14}
            />
        </button>
    );

    return (
        <div
            className={
                variant === 'floating'
                    ? styles.floatingContainer
                    : styles.compactContainer
            }
        >
            <UIPopover
                ref={popoverRef}
                triggerNode={triggerButton}
                placement={variant === 'floating' ? 'bottom_left' : 'top_right'}
                width={360}
            >
                <UIMenu>
                    <UIMenuItem
                        text={`${regionLabels.auto} (${regionLabels[autoDetectedRegion]})`}
                        description={t(
                            'components.region_selector.auto_description'
                        )}
                        leftImageProps={{
                            src: REGION_FLAGS[autoDetectedRegion],
                            alt: regionLabels.auto,
                            width: 20
                        }}
                        selected={selectedRegion === 'auto'}
                        onClick={() => handleRegionSelect('auto')}
                    />
                    <UIMenuItem
                        text={regionLabels.eu}
                        description={t(
                            'components.region_selector.eu_description'
                        )}
                        leftImageProps={{
                            src: REGION_FLAGS.eu,
                            alt: 'UK/EU',
                            width: 20
                        }}
                        selected={selectedRegion === 'eu'}
                        onClick={() => handleRegionSelect('eu')}
                    />
                    <UIMenuItem
                        text={regionLabels.us}
                        description={t(
                            'components.region_selector.us_description'
                        )}
                        leftImageProps={{
                            src: REGION_FLAGS.us,
                            alt: 'US',
                            width: 20
                        }}
                        selected={selectedRegion === 'us'}
                        onClick={() => handleRegionSelect('us')}
                    />
                </UIMenu>
            </UIPopover>
        </div>
    );
}
